import React from 'react';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import { useTranslation } from 'next-i18next';

import Box from '@mui/material/Box';

import Typography from 'src/components/ui/Typography';
import Checkbox from 'src/components/ui/Checkbox';
import Button from 'src/components/ui/Button';

import useUser from 'src/hooks/useUser';
import { colours } from 'src/constants/colours';
import { styledComponent } from 'src/utils/styled';
import useLoginModal from 'src/hooks/useLoginModal';
import { useWoopenSupportInfoQuery } from 'src/hooks/__generated__/queries';

const PagesJaunesOwnershipBox = styledComponent(Box)`
    padding: 2rem;
    border: solid 0.0625rem ${colours.black.O08};
    border-radius: 1rem;
    margin-top: 2rem;
    margin-bottom: 2rem;
`;

interface BecomeAdminFormValues {
    imPartOfThisBusiness: boolean;
}

interface Props {
    canBeClaimedByCurrentUser: boolean;
    openPagesJaunesClaimModal?: () => void;
}

const PagesJaunesOwnershipSection: React.FC<Props> = ({ canBeClaimedByCurrentUser, openPagesJaunesClaimModal }) => {
    const currentUser = useUser();
    const { createAuthenticatedHandler } = useLoginModal();
    const woopenSupportInfo = useWoopenSupportInfoQuery();
    const { t } = useTranslation(['common', 'company']);

    const handleBecomeAdminSubmit = async (values: BecomeAdminFormValues) => {
        await createAuthenticatedHandler(
            () => {
                if (values.imPartOfThisBusiness && canBeClaimedByCurrentUser) {
                    openPagesJaunesClaimModal?.();
                }
            },
            undefined,
            undefined,
            true,
        );
    };

    const becomeAdminSchema = Yup.object().shape({
        imPartOfThisBusiness: Yup.boolean().oneOf([true], ' '),
    });

    const canSubmit = !currentUser || canBeClaimedByCurrentUser;
    const mailSubject = `${t('common:iNeedHelp')} - ${t('company:pagesJaunesOwnership')}`;

    return (
        <Formik
            initialValues={{
                imPartOfThisBusiness: false,
            }}
            validationSchema={becomeAdminSchema}
            onSubmit={handleBecomeAdminSubmit}
        >
            {({ isValid }) => (
                <Form>
                    <PagesJaunesOwnershipBox>
                        <Typography variant="h2">{t('company:isThisYourBusinessTitle')}</Typography>
                        <Typography
                            marginTop="1rem"
                            marginBottom="0.75rem"
                            data-target-id="pagesjaunes-ownership-section-description"
                        >
                            {t(
                                canSubmit
                                    ? 'company:isThisYourBusinessDescription'
                                    : 'company:isThisYourBusinessUnableDescription',
                            )}
                        </Typography>
                        {canSubmit ? (
                            <>
                                <Checkbox
                                    data-target-id="pagesjaunes-ownership-section-checkbox"
                                    name="imPartOfThisBusiness"
                                    label={t('company:isThisYourBusinessCheckbox')}
                                    showErrorOnLabel
                                />
                                <Button
                                    data-target-id="pagesjaunes-ownership-section-submit"
                                    sx={{ marginTop: '1rem' }}
                                    disabled={!isValid}
                                    type="submit"
                                    fakeDisabled
                                >
                                    {t('company:isThisYourBusinessButton')}
                                </Button>
                            </>
                        ) : (
                            <>
                                <Typography
                                    data-target-id="pagesjaunes-ownership-section-more-infos"
                                    marginTop="1rem"
                                    marginBottom="1.2rem"
                                >
                                    {t('common:forMoreInfoContactCustomerService')}
                                </Typography>
                                <Button
                                    data-target-id="pagesjaunes-ownership-section-more-contact-button"
                                    variant="outlined"
                                    href={`mailto:${woopenSupportInfo.data?.supportEmail}?subject=${mailSubject}`}
                                >
                                    {t('common:contactCustomerService')}
                                </Button>
                            </>
                        )}
                    </PagesJaunesOwnershipBox>
                </Form>
            )}
        </Formik>
    );
};

export default PagesJaunesOwnershipSection;
